import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert, Chip, DanyComposer, DanyProviderResults, DanyStatus, EmptyState,
} from "@/ds";
import api, { mediaUrl } from "@/lib/api";
import { categoryLabel } from "@/lib/catalog";
import { NoneNearby, distanceLabel } from "@/components/dany/DanyWidgets";
import { TURNSTILE_SITE_KEY, loadTurnstileScript } from "@/lib/turnstile";
import { useSmartCTA } from "@/hooks/useSmartCTA";

/**
 * AI-assisted FixyBee search (G1), on Fable (DY1.4).
 *
 * **The surface changed; the contract did not.** BRIEF-FABLE.md Paso 5 is
 * explicit: this component's intent → category → ranked-providers flow is what
 * Dany inherits, so the API calls stay exactly as they were —
 * `POST /ai/search-intent` then `GET /users/providers/search` — along with the
 * ZIP requirement, the Turnstile challenge the backend raises for high-frequency
 * IPs, the rate-limit and quota branches, and the never-dead-end empty state.
 * What is gone is the old card-and-textarea chrome: the input is now
 * `DanyComposer`, progress is `DanyStatus`, and results are
 * `DanyProviderResults` reusing `ProviderCard` verbatim.
 *
 * It moved off the home in the same block. The home's search bar is now Dany's
 * hero composer, and a second bar under it was exactly the duplication Jorge's
 * spec removed — so this lives on `/providers`, where finding a FixyBee is the
 * job of the page. Fully public; the account gate is downstream at "request a
 * quote", not here.
 */
export default function AiSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const cta = useSmartCTA();
  const [text, setText] = useState("");
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [intent, setIntent] = useState(null); // { problem_summary, categories[], confidence }
  const [providers, setProviders] = useState(null); // null = not searched yet
  const [geoUsed, setGeoUsed] = useState(false);
  // The coverage radius the backend actually applied. Present only on a geo
  // search, and the difference between "no FixyBees within 50 miles of 79901"
  // and the old, uninformative "no results".
  const [radiusMiles, setRadiusMiles] = useState(null);
  const [locNotice, setLocNotice] = useState(false); // true when location was unavailable
  const [turnstileRequired, setTurnstileRequired] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Mount the Turnstile widget only once the backend has actually asked for
  // it (high-frequency IPs) — never shown up front.
  useEffect(() => {
    if (!turnstileRequired || !TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    let cancelled = false;
    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile || turnstileWidgetId.current) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(null),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [turnstileRequired]);

  // Same threshold as Dany's widget, from the same module: a result 1,600 miles
  // away is legitimate (the search widens when nothing is close) but the number
  // on the card reads as a bug. Past the limit the badge is dropped and the card
  // falls back to city and state.

  const onSubmit = async () => {
    if (!text.trim() || loading) return;
    const zipVal = zip.trim();
    // ZIP is required so results can be ranked by proximity to the user.
    if (!/^\d{5}$/.test(zipVal)) {
      setError(t("search.zipRequired"));
      return;
    }
    setLoading(true);
    setError("");
    setIntent(null);
    setProviders(null);
    setLocNotice(false);

    try {
      // 1) Interpret the problem and geocode the ZIP server-side.
      const { data: si } = await api.post("/ai/search-intent", {
        text: text.trim(),
        zip: zipVal,
        lat: null,
        lng: null,
        turnstile_token: turnstileToken || null,
      });
      setTurnstileRequired(false);
      setIntent(si);

      // 2) ZIP couldn't be resolved (not a US ZIP in the local table / another
      //    country) -> tell the user instead of silently ignoring it.
      const loc = si.location;
      if (!loc) {
        setError(t("search.zipNotFound"));
        return;
      }
      setGeoUsed(true);
      setLocNotice(false);

      // 3) No category match -> show empty state, skip provider fetch.
      const catIds = (si.categories || []).map((c) => c.category_id);
      if (catIds.length === 0) {
        setProviders([]);
        return;
      }

      // 4) Fetch providers ranked by distance from the ZIP.
      const params = new URLSearchParams();
      params.set("categories", catIds.join(","));
      params.set("lat", loc.lat);
      params.set("lng", loc.lng);
      const { data: pr } = await api.get(`/users/providers/search?${params}`);
      setProviders(pr.providers || []);
      setGeoUsed(!!pr.geo);
      setRadiusMiles(pr.radius_miles ?? null);
      if (!pr.geo) setLocNotice(true);
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "verification_required") {
        setTurnstileRequired(true);
        setTurnstileToken(null);
        setError(t("search.verificationRequired"));
      } else if (code === "ai_quota_exceeded" || err?.response?.status === 429) {
        setError(t("search.rateLimited"));
      } else {
        setError(t("search.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ width: "100%" }} data-testid="ai-search">
      <DanyComposer
        size="inline"
        value={text}
        onChange={setText}
        onSubmit={onSubmit}
        zip={zip}
        onZipChange={setZip}
        zipPlaceholder={t("search.zipPlaceholder")}
        zipLabel={t("search.zipPlaceholder")}
        placeholder={t("search.placeholder")}
        placeholderShort={t("search.placeholderShort")}
        attachments={false}
        voiceDisabled
        voiceTitle={t("dany.voice.comingSoon")}
      />

      {loading && (
        <div style={{ marginTop: "var(--space-4)" }} data-testid="ai-search-loading">
          <DanyStatus state="retrieving" label={t("search.searching")} detail={zip.trim() || undefined} />
        </div>
      )}

      {error && (
        <div style={{ marginTop: "var(--space-4)" }} data-testid="ai-search-error">
          <Alert tone="danger">{error}</Alert>
        </div>
      )}

      {/* Only mounted when the backend flags this IP as high-frequency. */}
      {turnstileRequired && TURNSTILE_SITE_KEY && (
        <div style={{ marginTop: "var(--space-4)" }} ref={turnstileRef} data-testid="ai-search-turnstile" />
      )}

      {/* Detected intent — what the model understood, in the user's own terms. */}
      {intent && (
        <div style={{ marginTop: "var(--space-5)" }} data-testid="ai-search-intent">
          {intent.problem_summary && (
            <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", lineHeight: "var(--leading-relaxed)" }}>
              <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, color: "var(--text-body)" }}>
                {t("search.understood")}:
              </span>{" "}
              {intent.problem_summary}
            </p>
          )}
          {intent.categories?.length > 0 && (
            <div style={{ marginTop: "var(--space-3)", display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
              {intent.categories.map((c) => (
                <Chip
                  key={c.category_id}
                  icon="wrench"
                  onClick={() => navigate(`/providers?category=${c.category_id}`)}
                  data-testid={`intent-cat-${c.category_id}`}
                >
                  {categoryLabel(t, c)}
                </Chip>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Location notice (never dead-ends the user) */}
      {providers && providers.length > 0 && locNotice && (
        <p
          style={{
            marginTop: "var(--space-3)", display: "flex", alignItems: "center", gap: 4,
            fontSize: "var(--text-xs)", color: "var(--text-muted)",
          }}
          data-testid="ai-search-loc-notice"
        >
          {t("search.locationHint")}
        </p>
      )}

      {providers && providers.length > 0 && (
        <div style={{ marginTop: "var(--space-5)" }} data-testid="ai-search-results">
          <DanyProviderResults
            caption={t("search.resultsCaption", { count: providers.length })}
            providers={providers.map((p) => ({
              id: p.user_id,
              name: p.full_name,
              company: p.company_name || p.full_name,
              city: p.location_city,
              state: p.location_state,
              avatarUrl: p.avatar_url ? mediaUrl(p.avatar_url) : undefined,
              verified: !!p.is_verified,
              rating: p.average_rating || 0,
              jobsCount: p.completed_jobs_count || 0,
              bio: p.bio,
              distance: geoUsed ? distanceLabel(p.distance_miles) : null,
              ctaLabel: t("providers.visit"),
              verifiedLabel: t("providers.verified"),
              newLabel: t("providers.newHere"),
              jobsLabel: t("providers.jobsDone", { count: p.completed_jobs_count || 0 }),
              onVisit: p.public_slug ? () => navigate(`/pro/${p.public_slug}`) : undefined,
            }))}
          />
        </div>
      )}

      {/* Empty state — never dead-end.

          Two different empties. The category was understood but nobody covers
          this ZIP: say the radius and the place, and invite a professional,
          which is the same treatment Dany gives it in the thread. Or nothing
          matched the words at all, which is the old copy — a radius is not the
          reason and quoting one would be misleading. */}
      {providers && providers.length === 0 && (
        <div style={{ marginTop: "var(--space-5)" }} data-testid="ai-search-empty">
          {radiusMiles && intent?.categories?.length > 0 ? (
            <NoneNearby radiusMiles={radiusMiles} zip={zip.trim()} />
          ) : (
            <EmptyState
              title={intent?.categories?.length === 0 ? t("search.noMatch") : t("search.noProviders")}
              primaryAction={{
                label: t("search.postJob"),
                // `cta.postJob()` rather than a hard-coded /signup: this
                // component renders for signed-in readers too, and the old
                // line sent a client who already had an account to register
                // again. Anonymous readers get /signup with a returnTo, which
                // is what `intent=post-job` never did.
                onClick: () => navigate(cta.postJob()),
              }}
              secondaryAction={{
                label: t("search.browseAll"),
                onClick: () => navigate("/providers"),
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}
